/**
 * DQ-346: the cashless hospital network.
 *
 * Public locator: `GET /directory/hospitals/` (AllowAny, the directory is shown
 * to everyone). Insurer lists: `/directory/hospital-imports/` (dry-run preview,
 * then commit) and `/directory/insurer-hospitals/` (an insurer's own mappings).
 * Country admin: `/directory/hospital-matches/` (doubtful-match queue),
 * `/directory/hospital-network/` and merge/unmerge on a hospital.
 */
import api from './api';

export type NetworkScope = 'all' | 'unique' | 'partial' | 'none';

export interface InsurerChip {
  id: string;
  name: string;
}

export interface LocatorProvider {
  provider_id: string;
  name: string;
  phone: string;
  address?: string;
  city_name?: string;
  state_name?: string;
  pincode: string;
  latitude: string | null;
  longitude: string | null;
  emergency_available?: boolean;
  ambulance_available?: boolean;
  cashless_available?: boolean;
  network_scope?: NetworkScope;
  unique_to_insurer?: InsurerChip | null;
  cashless_insurers?: InsurerChip[];
  distance?: number | null;
}

export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface LocatorFilters {
  q?: string;
  city?: string;
  pincode?: string;
  insurer?: string;
  cashless?: boolean;
  page?: number;
}

export interface MyHealthInsurer extends InsurerChip {
  policy_id: string;
  policy_number: string;
}

export interface HospitalSummary {
  id: string;
  name: string;
  provider_code: string;
  address: string;
  city_name: string;
  pincode: string;
  phone: string;
  registration_number: string;
  network_scope: NetworkScope;
  status: string;
  merged_into: string | null;
  insurers?: string[];
  score?: number;
  reason?: string;
}

export interface RowIssue {
  column: string;
  message: string;
}

export type ImportOutcome = 'new' | 'matched_auto' | 'doubtful' | 'invalid' | 'unchanged';

export interface ImportRow {
  id: string;
  row_number: number;
  hospital_name: string;
  raw: Record<string, string>;
  errors: RowIssue[];
  warnings: RowIssue[];
  outcome: ImportOutcome;
  matched_hospital: HospitalSummary | null;
  match_reason: string;
  match_score: string | null;
  candidates: HospitalSummary[];
  resolution: string;
  resolution_note: string;
  resolved_by_name?: string;
  resolved_at: string | null;
}

export interface MatchRow extends ImportRow {
  insurer: string;
  insurer_name: string;
  batch: string;
  batch_name: string;
  incoming: Record<string, unknown>;
  created_at: string;
}

export interface ImportBatch {
  id: string;
  insurer: string;
  insurer_name: string;
  uploaded_by_name: string;
  original_name: string;
  status: 'previewed' | 'committed' | 'discarded' | 'failed';
  status_display: string;
  is_full_list: boolean;
  row_count: number;
  new_count: number;
  matched_count: number;
  doubtful_count: number;
  invalid_count: number;
  unchanged_count: number;
  will_remove_count: number;
  /** Rows skipped because the insurer removed that hospital by hand (R2-F9). */
  held_back_count?: number;
  removed_count: number;
  pending_review_count: number;
  missing_registration_count: number;
  created_at: string;
  committed_at: string | null;
}

export interface ImportBatchDetail extends ImportBatch {
  rows_by_outcome: Record<ImportOutcome, ImportRow[]>;
  will_remove: HospitalSummary[];
}

export interface InsurerHospital {
  id: string;
  hospital: string;
  hospital_name: string;
  address: string;
  city_name: string;
  pincode: string;
  registration_number: string;
  network_scope: NetworkScope;
  insurer: string;
  insurer_name: string;
  cashless_enabled: boolean;
  insurer_hospital_code: string;
  tpa_name: string;
  valid_from: string | null;
  valid_to: string | null;
  status: 'active' | 'inactive';
  last_confirmed_at: string | null;
}

export interface NetworkHospitalRow {
  provider_id: string;
  name: string;
  provider_code: string;
  city_name: string;
  pincode: string;
  registration_number: string;
  status: string;
  network_scope: NetworkScope;
  insurer_count: number;
  insurers: { id: string; name: string; cashless_enabled: boolean }[];
  merged_into: string | null;
  merged_into_name: string;
  merged_at: string | null;
}

export interface NetworkSummary {
  scope_counts: Record<NetworkScope, number>;
  per_insurer: { id: string; name: string; mapped: number }[];
  pending_review: number;
  pending_over_30_days: number;
  latest_batches: ImportBatch[];
}

export type ResolveAction = 'link' | 'create_new' | 'skip' | 'request_correction';

function clean(params: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    out[key] = String(value);
  }
  return out;
}

const MAX_LIST_BYTES = 10 * 1024 * 1024;

/** Why this hospital list cannot be uploaded, or null. The server checks again. */
export function validateHospitalListFile(file: File): string | null {
  const name = file.name.toLowerCase();
  if (!name.endsWith('.csv') && !name.endsWith('.xlsx')) return 'Upload the list as a .csv or .xlsx file.';
  if (!file.size) return 'The file you chose is empty.';
  if (file.size > MAX_LIST_BYTES) return 'The file is larger than 10 MB. Split it and upload the parts.';
  return null;
}

/** A 6-digit pincode, or a prefix of one. Anything else is not sent at all. */
export function isPincodeQuery(value: string): boolean {
  return /^[0-9]{0,6}$/.test(value);
}

/** The card badge: "All insurers", "Only {insurer}" or "{n} insurers". */
export function networkBadge(provider: LocatorProvider): string | null {
  const insurers = provider.cashless_insurers ?? [];
  if (provider.network_scope === 'all') return 'All insurers';
  if (provider.network_scope === 'unique') {
    const only = provider.unique_to_insurer?.name ?? insurers[0]?.name;
    return only ? `Only ${only}` : null;
  }
  if (provider.network_scope === 'partial') return `${insurers.length || 'Several'} insurers`;
  return null;
}

export function directionsUrl(provider: LocatorProvider): string {
  if (provider.latitude && provider.longitude) {
    return `https://www.google.com/maps/search/?api=1&query=${provider.latitude},${provider.longitude}`;
  }
  const address = [provider.name, provider.address, provider.city_name, provider.pincode].filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

const locatorBase = {
  hospital: '/directory/hospitals/',
  garage: '/directory/garages/',
} as const;

export type LocatorKind = keyof typeof locatorBase;

export const hospitalNetworkApi = {
  async search(kind: LocatorKind, filters: LocatorFilters): Promise<Paginated<LocatorProvider>> {
    const params = clean({
      q: filters.q?.trim(),
      city: filters.city?.trim(),
      pincode: filters.pincode?.trim(),
      page: filters.page && filters.page > 1 ? filters.page : undefined,
      ...(kind === 'hospital'
        ? { insurer: filters.insurer, cashless: filters.cashless ? 'true' : undefined }
        : { cashless_available: filters.cashless ? 'true' : undefined }),
    });
    const response = await api.get<Paginated<LocatorProvider>>(locatorBase[kind], { params });
    return response.data;
  },

  async networkInsurers(): Promise<InsurerChip[]> {
    const response = await api.get<InsurerChip[]>('/directory/hospitals/network-insurers/');
    return response.data;
  },

  async myInsurers(): Promise<MyHealthInsurer[]> {
    const response = await api.get<MyHealthInsurer[]>('/directory/hospitals/my-insurers/');
    return response.data;
  },

  // ── Insurer hospital lists ──
  async listBatches(params: { insurer?: string; status?: string; page?: number } = {}): Promise<Paginated<ImportBatch>> {
    const response = await api.get<Paginated<ImportBatch>>('/directory/hospital-imports/', { params: clean(params) });
    return response.data;
  },

  async getBatch(id: string): Promise<ImportBatchDetail> {
    const response = await api.get<ImportBatchDetail>(`/directory/hospital-imports/${id}/`);
    return response.data;
  },

  async preview(input: { file: File; isFullList: boolean; insurer?: string }): Promise<ImportBatchDetail> {
    const form = new FormData();
    form.append('file', input.file);
    form.append('is_full_list', input.isFullList ? 'true' : 'false');
    if (input.insurer) form.append('insurer', input.insurer);
    const response = await api.post<ImportBatchDetail>('/directory/hospital-imports/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return response.data;
  },

  async commit(id: string): Promise<ImportBatchDetail> {
    const response = await api.post<ImportBatchDetail>(`/directory/hospital-imports/${id}/commit/`, {}, { timeout: 120000 });
    return response.data;
  },

  async discard(id: string): Promise<ImportBatch> {
    const response = await api.post<ImportBatch>(`/directory/hospital-imports/${id}/discard/`, {});
    return response.data;
  },

  async downloadTemplate(): Promise<void> {
    await saveBlob('/directory/hospital-imports/template/', 'hospital-list-template.csv');
  },

  async downloadErrors(id: string): Promise<void> {
    await saveBlob(`/directory/hospital-imports/${id}/errors.csv/`, `hospital-list-errors-${id}.csv`);
  },

  async listMappings(params: { q?: string; status?: string; insurer?: string; page?: number } = {}): Promise<Paginated<InsurerHospital>> {
    const response = await api.get<Paginated<InsurerHospital>>('/directory/insurer-hospitals/', { params: clean(params) });
    return response.data;
  },

  async updateMapping(id: string, patch: Partial<Pick<InsurerHospital, 'cashless_enabled' | 'status'>>): Promise<InsurerHospital> {
    const response = await api.patch<InsurerHospital>(`/directory/insurer-hospitals/${id}/`, patch);
    return response.data;
  },

  async deactivateMapping(id: string): Promise<void> {
    await api.delete(`/directory/insurer-hospitals/${id}/`);
  },

  // ── Country admin ──
  async listMatches(params: { status?: string; insurer?: string; page?: number } = {}): Promise<Paginated<MatchRow>> {
    const response = await api.get<Paginated<MatchRow>>('/directory/hospital-matches/', { params: clean(params) });
    return response.data;
  },

  async resolveMatch(id: string, body: { action: ResolveAction; hospital_id?: string; note?: string }): Promise<MatchRow> {
    const response = await api.post<MatchRow>(`/directory/hospital-matches/${id}/resolve/`, body);
    return response.data;
  },

  async networkSummary(): Promise<NetworkSummary> {
    const response = await api.get<NetworkSummary>('/directory/hospital-network/summary/');
    return response.data;
  },

  async networkHospitals(
    params: { q?: string; scope?: string; insurer?: string; merged?: string; page?: number } = {}
  ): Promise<Paginated<NetworkHospitalRow>> {
    const response = await api.get<Paginated<NetworkHospitalRow>>('/directory/hospital-network/', { params: clean(params) });
    return response.data;
  },

  async merge(sourceId: string, intoId: string, note: string): Promise<void> {
    await api.post(`/directory/hospitals/${sourceId}/merge/`, { into: intoId, note });
  },

  async unmerge(sourceId: string, note = ''): Promise<void> {
    await api.post(`/directory/hospitals/${sourceId}/unmerge/`, { note });
  },
};

async function saveBlob(url: string, filename: string): Promise<void> {
  const response = await api.get(url, { responseType: 'blob' });
  const href = window.URL.createObjectURL(response.data as Blob);
  const link = window.document.createElement('a');
  link.href = href;
  link.download = filename;
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);
  window.URL.revokeObjectURL(href);
}
